import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Link
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';

interface TenantFormData {
  name: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
  ownerPassword: string;
  ownerPasswordConfirm: string;
  subscriptionTier: string;
  agreeToTerms: boolean;
}

const TenantSignup: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<TenantFormData>({
    name: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    ownerFirstName: '',
    ownerLastName: '',
    ownerEmail: '',
    ownerPassword: '',
    ownerPasswordConfirm: '',
    subscriptionTier: 'basic',
    agreeToTerms: false
  });

  const steps = ['Business Information', 'Owner Account', 'Confirmation'];

  const handleNext = () => {
    if (activeStep === 0 && !validateBusinessInfo()) return;
    if (activeStep === 1 && !validateOwnerInfo()) return;
    
    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const validateBusinessInfo = () => {
    if (!formData.name || !formData.email || !formData.phone) {
      setError('Please fill in all business information fields');
      return false;
    }
    if (!formData.address.street || !formData.address.city || !formData.address.state || !formData.address.pincode) {
      setError('Please fill in all address fields');
      return false;
    }
    setError(null);
    return true;
  };

  const validateOwnerInfo = () => {
    if (!formData.ownerFirstName || !formData.ownerLastName || !formData.ownerEmail || !formData.ownerPassword) {
      setError('Please fill in all owner information fields');
      return false;
    }
    if (formData.ownerPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (formData.ownerPassword !== formData.ownerPasswordConfirm) {
      setError('Passwords do not match');
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmit = async () => {
    if (!formData.agreeToTerms) {
      setError('You must agree to the terms and conditions');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { ownerPasswordConfirm, agreeToTerms, ...submitData } = formData;
      const response = await apiClient.post('/tenants', submitData);

      if (response.success) {
        navigate('/login', {
          state: {
            message: 'Account created successfully! Your 14-day free trial has started. Please log in.',
            trialEndsAt: response.data.salon.trialEndsAt
          }
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderBusinessInfoStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Business Details
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Salon Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Business Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Phone Number"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          placeholder="+91-XXXXXXXXXX"
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          Address
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          label="Street Address"
          value={formData.address.street}
          onChange={(e) => setFormData({
            ...formData,
            address: { ...formData.address, street: e.target.value }
          })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="City"
          value={formData.address.city}
          onChange={(e) => setFormData({
            ...formData,
            address: { ...formData.address, city: e.target.value }
          })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="State"
          value={formData.address.state}
          onChange={(e) => setFormData({
            ...formData,
            address: { ...formData.address, state: e.target.value }
          })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Pincode"
          value={formData.address.pincode}
          onChange={(e) => setFormData({
            ...formData,
            address: { ...formData.address, pincode: e.target.value }
          })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Country"
          value={formData.address.country}
          onChange={(e) => setFormData({
            ...formData,
            address: { ...formData.address, country: e.target.value }
          })}
        />
      </Grid>
    </Grid>
  );

  const renderOwnerInfoStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Owner Account
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="First Name"
          value={formData.ownerFirstName}
          onChange={(e) => setFormData({ ...formData, ownerFirstName: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Last Name"
          value={formData.ownerLastName}
          onChange={(e) => setFormData({ ...formData, ownerLastName: e.target.value })}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          label="Email"
          type="email"
          value={formData.ownerEmail}
          onChange={(e) => setFormData({ ...formData, ownerEmail: e.target.value })}
          helperText="This will be your login email"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Password"
          type="password"
          value={formData.ownerPassword}
          onChange={(e) => setFormData({ ...formData, ownerPassword: e.target.value })}
          helperText="Minimum 8 characters"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Confirm Password"
          type="password"
          value={formData.ownerPasswordConfirm}
          onChange={(e) => setFormData({ ...formData, ownerPasswordConfirm: e.target.value })}
        />
      </Grid>
    </Grid>
  );

  const renderConfirmationStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Review & Confirm
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Business Information
          </Typography>
          <Typography variant="body2"><strong>Name:</strong> {formData.name}</Typography>
          <Typography variant="body2"><strong>Email:</strong> {formData.email}</Typography>
          <Typography variant="body2"><strong>Phone:</strong> {formData.phone}</Typography>
          <Typography variant="body2"><strong>Address:</strong> {formData.address.street}, {formData.address.city}, {formData.address.state} {formData.address.pincode}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Owner Account
          </Typography>
          <Typography variant="body2"><strong>Name:</strong> {formData.ownerFirstName} {formData.ownerLastName}</Typography>
          <Typography variant="body2"><strong>Email:</strong> {formData.ownerEmail}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>14-Day Free Trial</strong>
          </Typography>
          <Typography variant="body2">
            Your trial includes full access to all Basic tier features. No credit card required.
          </Typography>
        </Alert>
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Checkbox
            checked={formData.agreeToTerms}
            onChange={(e) => setFormData({ ...formData, agreeToTerms: e.target.checked })}
            sx={{ mt: -1 }}
          />
          <Typography variant="body2" sx={{ mt: 1 }}>
            I agree to the{' '}
            <Link href="#" onClick={(e) => e.preventDefault()}>
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link href="#" onClick={(e) => e.preventDefault()}>
              Privacy Policy
            </Link>
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderBusinessInfoStep();
      case 1:
        return renderOwnerInfoStep();
      case 2:
        return renderConfirmationStep();
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3
      }}
    >
      <Card sx={{ maxWidth: 800, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" align="center" gutterBottom>
            Start Your Free Trial
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
            Get started with a 14-day free trial. No credit card required.
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            {getStepContent(activeStep)}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading}
            >
              {activeStep === steps.length - 1 ? 'Create Account' : 'Next'}
            </Button>
          </Box>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/login')}
              >
                Sign in
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TenantSignup;
